<template>
    <div class="timetable-container">
      <div class="header-section">
        <h1 class="page-title">School Timetable</h1>
        <div class="action-buttons">
          <timetable-add-form :api="api" @entry-added="fetchTimetable" />
          <holiday-manager :api="api" @holiday-changed="fetchTimetable" />
        </div>
      </div>
      
      <el-card shadow="hover" class="control-panel">
        <div class="controls">
          <el-select 
            v-model="viewType" 
            @change="fetchTimetable"
            class="view-selector"
            placeholder="View Type"
          >
            <el-option value="day" label="Day View" />
            <el-option value="week" label="Week View" />
          </el-select>
          
          <div class="navigation-controls">
            <el-button 
              @click="previousPeriod" 
              circle
              :icon="ArrowLeft"
              class="nav-button"
            />
            <div class="current-period">{{ currentPeriodLabel }}</div>
            <el-button 
              @click="nextPeriod" 
              circle
              :icon="ArrowRight"
              class="nav-button"
            />
          </div>
          
          <el-select 
            v-model="selectedClass" 
            placeholder="Select Class"
            clearable
            class="class-selector"
          >
            <el-option 
              v-for="classItem in classes" 
              :key="classItem.id"
              :label="classItem.name" 
              :value="classItem.id" 
            />
          </el-select>
    
          <el-select 
            v-model="selectedSection" 
            @change="fetchTimetable"
            placeholder="Select Section"
            clearable
            :disabled="!selectedClass"
            class="section-selector"
          >
            <el-option 
              v-for="section in filteredSections" 
              :key="section.id"
              :label="section.name" 
              :value="section.id" 
            />
          </el-select>
        </div>
      </el-card>
    
      <!-- Day View -->
      <el-card shadow="hover" v-if="viewType === 'day'" class="day-view">
        <h2 class="day-title">{{ currentDate.format('dddd, MMMM D, YYYY') }}</h2>
        
        <el-empty 
          v-if="isHoliday" 
          description="No classes scheduled for today"
          class="holiday-notice"
        >
          <template #description>
            <h3 class="holiday-title">Holiday: {{ holidayName }}</h3>
            <p>No classes scheduled for today.</p>
          </template>
          <el-icon :size="60" color="var(--el-color-warning)">
            <Calendar />
          </el-icon>
        </el-empty>
        
        <div v-else class="schedule-grid">
          <div class="time-column">
            <div class="time-header">
              <el-icon><Clock /></el-icon>
              <span>Time</span>
            </div>
            <div v-for="period in allTimeSlots" class="time-slot">
              {{ period.display_start }} - {{ period.display_end }}
            </div>
          </div>
          
          <div class="periods-column">
            <div class="period-header">
              <el-icon><School /></el-icon>
              <span>Classes</span>
            </div>
            <div 
              v-for="period in allTimeSlots" 
              class="period-slot"
              :class="{ 'empty-slot': !getPeriodAtTime(period.start, period.end) }"
            >
              <div v-if="getPeriodAtTime(period.start, period.end)" class="period-card">
                <div class="period-header">
                  <div class="period-subject">{{ getPeriodAtTime(period.start, period.end).subject_name }}</div>
                  <div class="period-actions">
                    <el-button 
                      @click.stop="editEntry(getPeriodAtTime(period.start, period.end))" 
                      size="small" 
                      circle
                    >
                      <el-icon><Edit /></el-icon>
                    </el-button>
                    <el-button 
                      @click.stop="confirmDelete(getPeriodAtTime(period.start, period.end).id)" 
                      size="small" 
                      circle 
                      type="danger"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>
                </div>
                <div class="period-details">
                  <div v-if="getPeriodAtTime(period.start, period.end).teacher_name" class="period-teacher">
                    <el-icon><User /></el-icon>
                    {{ getPeriodAtTime(period.start, period.end).teacher_name }}
                  </div>
                  <div class="period-class-section">
                    <el-icon><Reading /></el-icon>
                    <span v-if="getPeriodAtTime(period.start, period.end).class_name">
                      {{ getPeriodAtTime(period.start, period.end).class_name }}
                    </span>
                    <span v-if="getPeriodAtTime(period.start, period.end).section_name">
                      /{{ getPeriodAtTime(period.start, period.end).section_name }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="free-period">
                <el-icon><CoffeeCup /></el-icon>
                <span>Free Period</span>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    
      <!-- Week View -->
      <el-card shadow="hover" v-else class="week-view">
        <h2 class="week-title">Week of {{ weekStart.format('MMMM D') }} - {{ weekEnd.format('MMMM D, YYYY') }}</h2>
        
        <div class="week-schedule">
          <div class="week-time-column">
            <div class="corner-cell"></div>
            <div v-for="period in timeSlots" class="week-time-slot">
              {{ period.start }} - {{ period.end }}
            </div>
          </div>
          
          <div 
            v-for="day in weekTimetable" 
            class="week-day-column"
            :class="{ 'holiday-column': day.is_holiday }"
          >
            <div class="day-header">
              <h3>{{ day.day_name }}</h3>
              <div>{{ day.date }}</div>
              <el-tag 
                v-if="day.is_holiday" 
                type="warning" 
                size="small" 
                class="holiday-badge"
              >
                {{ day.holiday_name }}
              </el-tag>
            </div>
            
            <div 
              v-for="period in timeSlots" 
              class="week-period-slot"
              :class="{ 'holiday-slot': day.is_holiday }"
            >
              <div v-if="!day.is_holiday && getPeriodAtTimeForDay(day, period.start, period.end)" class="week-period-card">
                <div class="period-header">
                  <div class="week-period-subject">{{ getPeriodAtTimeForDay(day, period.start, period.end).subject_name }}</div>
                  <div class="period-actions">
                    <el-button 
                      @click.stop="editEntry(getPeriodAtTimeForDay(day, period.start, period.end))" 
                      size="small" 
                      circle
                    >
                      <el-icon><Edit /></el-icon>
                    </el-button>
                    <el-button 
                      @click.stop="confirmDelete(getPeriodAtTimeForDay(day, period.start, period.end).id)" 
                      size="small" 
                      circle 
                      type="danger"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>
                </div>
                <div v-if="getPeriodAtTimeForDay(day, period.start, period.end).teacher_name" class="week-period-teacher">
                  <el-icon size="12"><User /></el-icon>
                  {{ getPeriodAtTimeForDay(day, period.start, period.end).teacher_name }}
                </div>
              </div>
              <div v-else-if="day.is_holiday" class="holiday-message">
                <el-icon><Calendar /></el-icon>
                <span>Holiday</span>
              </div>
              <div v-else class="week-free-period"></div>
            </div>
          </div>
        </div>
      </el-card>
  
      <!-- Edit Dialog -->
      <el-dialog 
        v-model="editDialogVisible" 
        :title="`${isEditing ? 'Edit' : 'Add'} Timetable Entry`" 
        width="50%"
      >
        <timetable-add-form 
          v-if="editDialogVisible"
          :api="api" 
          :entry="currentEditEntry"
          :is-editing="isEditing"
          @entry-added="handleEntryUpdated"
          @cancel="editDialogVisible = false"
        />
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ref, computed, onMounted, watch } from 'vue';
  import { useStore } from 'vuex';
  import dayjs from 'dayjs';
  import { ElMessage, ElMessageBox } from 'element-plus';
  import TimetableAddForm from './TimetableAddForm.vue';
  import HolidayManager from './HolidayManager.vue';
  import {
    ArrowLeft,
    ArrowRight,
    Clock,
    School,
    User,
    Reading,
    CoffeeCup,
    Calendar,
    Edit,
    Delete
  } from '@element-plus/icons-vue';
  
  const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api',
  });
  
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  export default {
    name: 'TimeTable',
    components: {
      TimetableAddForm,
      HolidayManager
    },
    setup() {
      const store = useStore();
      const currentDate = ref(dayjs());
      const viewType = ref('day');
      const dayTimetable = ref([]);
      const weekTimetable = ref([]);
      const weekStart = ref(dayjs().startOf('week'));
      const weekEnd = ref(dayjs().endOf('week'));
      const isHoliday = ref(false);
      const holidayName = ref('');
      const selectedClass = ref('');
      const selectedSection = ref('');
      const classes = ref([]);
      const sections = ref([]);
      const editDialogVisible = ref(false);
      const currentEditEntry = ref(null);
      const isEditing = ref(false);
      
      // Define your standard time slots (these will be used as fallback)
      const formatTo12Hour = (time24) => {
        if (!time24) return '';
        const [hours, minutes] = time24.split(':');
        const period = +hours >= 12 ? 'PM' : 'AM';
        const hours12 = +hours % 12 || 12;
        return `${hours12}:${minutes} ${period}`;
      };
  
      // Define standard time slots in 12-hour format
      const timeSlots = ref([
        { start: '08:00', end: '08:45' },
        { start: '08:45', end: '09:30' },
        { start: '09:30', end: '10:15' },
        { start: '10:15', end: '11:00' },
        { start: '11:00', end: '11:45' },
        { start: '11:45', end: '12:30' },
        { start: '12:30', end: '13:15' },
        { start: '13:15', end: '14:00' },
        { start: '14:00', end: '14:45' },
        { start: '14:45', end: '15:30' },
        { start: '15:30', end: '16:15' },
        { start: '16:15', end: '17:00' }
      ]);
      
      // Computed property that combines predefined slots with actual periods from data
      const allTimeSlots = computed(() => {
        // Get all unique time slots from the actual timetable data
        const periodSlots = orderedDayTimetable.value.map(period => ({
          start: period.start_time,
          end: period.end_time,
          display_start: formatTo12Hour(period.start_time),
          display_end: formatTo12Hour(period.end_time)
        }));
          
        // Combine with predefined slots and remove duplicates
        const allSlots = [
          ...timeSlots.value.map(slot => ({
            start: slot.start,
            end: slot.end,
            display_start: formatTo12Hour(slot.start),
            display_end: formatTo12Hour(slot.end)
          })),
          ...periodSlots
        ];
        
        const uniqueSlots = [];
        const seen = new Set();
        
        allSlots.forEach(slot => {
          const key = `${slot.start}-${slot.end}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueSlots.push(slot);
          }
        });
        
        // Sort by start time
        return uniqueSlots.sort((a, b) => a.start.localeCompare(b.start));
      });
  
      const orderedDayTimetable = computed(() => {
        return [...dayTimetable.value].sort((a, b) => {
          return a.start_time.localeCompare(b.start_time);
        });
      });
  
      const getPeriodAtTime = (start, end) => {
        return orderedDayTimetable.value.find(period => 
          period.start_time === start && period.end_time === end
        );
      };
     
      const getPeriodAtTimeForDay = (day, start, end) => {
        if (!day.periods || day.periods.length === 0) return null;
        
        // Convert time strings to minutes for easier comparison
        const timeToMinutes = (timeStr) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        };
        
        const slotStart = timeToMinutes(start);
        const slotEnd = timeToMinutes(end);
        
        // Find the first period that overlaps with the time slot
        return day.periods.find(period => {
          const periodStart = timeToMinutes(period.start_time);
          const periodEnd = timeToMinutes(period.end_time);
          
          // Check if periods overlap
          return (periodStart < slotEnd && periodEnd > slotStart);
        });
      };
  
      const filteredSections = computed(() => {
        if (!selectedClass.value || !Array.isArray(sections.value)) return [];
        return sections.value.filter(section => section.class_id == selectedClass.value);
      });
      
      const currentPeriodLabel = computed(() => {
        if (viewType.value === 'day') {
          return currentDate.value.format('MMMM D, YYYY');
        } else {
          return `Week of ${weekStart.value.format('MMM D')} - ${weekEnd.value.format('MMM D, YYYY')}`;
        }
      });
      
      const fetchClasses = async () => {
        try {
          const response = await api.get('/classes');
          classes.value = response.data;
        } catch (error) {
          console.error('Error fetching classes:', error);
        }
      };
      
      const fetchSectionsForClass = async (classId) => {
        try {
          if (!classId) {
            sections.value = [];
            return;
          }
          const response = await api.get(`/classes/${classId}/sections`);
          sections.value = response.data.sections;
        } catch (error) {
          console.error('Error fetching sections:', error);
        }
      };
      
      watch(selectedClass, (newClassId) => {
        selectedSection.value = ''; // Reset section when class changes
        fetchSectionsForClass(newClassId);
        fetchTimetable();
      });
      
      const fetchTimetable = async () => {
        try {
          const params = {
            date: currentDate.value.format('YYYY-MM-DD'),
            week: viewType.value === 'week' ? true : false,
          };
          
          if (selectedClass.value) params.class_id = selectedClass.value;
          if (selectedSection.value) params.section_id = selectedSection.value;
          
          const response = await api.get('/timetable', { params });
          
          if (viewType.value === 'day') {
            dayTimetable.value = response.data.periods;
            isHoliday.value = response.data.is_holiday;
            holidayName.value = response.data.holiday_name || '';
          } else {
            weekTimetable.value = response.data.timetable;
            weekStart.value = dayjs(response.data.week_start);
            weekEnd.value = dayjs(response.data.week_end);
          }
        } catch (error) {
          console.error('Error fetching timetable:', error);
        }
      };
  
      const updateTimetableEntry = async (entry) => {
        try {
          await api.put(`/timetable/${entry.id}`, entry);
          fetchTimetable();
          ElMessage.success('Timetable entry updated successfully');
        } catch (error) {
          console.error('Error updating timetable entry:', error);
          ElMessage.error('Failed to update timetable entry');
        }
      };
  
      const deleteTimetableEntry = async (id) => {
        try {
          await api.delete(`/timetable/${id}`);
          fetchTimetable();
          ElMessage.success('Timetable entry deleted successfully');
        } catch (error) {
          console.error('Error deleting timetable entry:', error);
          ElMessage.error('Failed to delete timetable entry');
        }
      };
  
      const confirmDelete = (id) => {
        ElMessageBox.confirm(
          'Are you sure you want to delete this timetable entry?',
          'Warning',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }
        ).then(() => {
          deleteTimetableEntry(id);
        }).catch(() => {
          // Cancel action
        });
      };
  
      const editEntry = (entry) => {
        isEditing.value = true;
        currentEditEntry.value = { ...entry };
        editDialogVisible.value = true;
      };
  
      const handleEntryUpdated = () => {
        editDialogVisible.value = false;
        fetchTimetable();
      };
      
      const previousPeriod = () => {
        if (viewType.value === 'day') {
          currentDate.value = currentDate.value.subtract(1, 'day');
        } else {
          currentDate.value = currentDate.value.subtract(1, 'week');
        }
        fetchTimetable();
      };
      
      const nextPeriod = () => {
        if (viewType.value === 'day') {
          currentDate.value = currentDate.value.add(1, 'day');
        } else {
          currentDate.value = currentDate.value.add(1, 'week');
        }
        fetchTimetable();
      };
      
      onMounted(() => {
        fetchClasses();
        fetchTimetable();
      });
      
      return {
        api,
        currentDate,
        viewType,
        dayTimetable,
        orderedDayTimetable,
        weekTimetable,
        weekStart,
        weekEnd,
        isHoliday,
        holidayName,
        selectedClass,
        selectedSection,
        classes,
        sections,
        timeSlots,
        allTimeSlots,
        filteredSections,
        currentPeriodLabel,
        editDialogVisible,
        currentEditEntry,
        isEditing,
        fetchTimetable,
        previousPeriod,
        nextPeriod,
        getPeriodAtTime,
        getPeriodAtTimeForDay,
        formatTo12Hour,
        updateTimetableEntry,
        deleteTimetableEntry,
        confirmDelete,
        editEntry,
        handleEntryUpdated,
        // icons 
        ArrowLeft,
        ArrowRight,
        Clock,
        School,
        User,
        Reading,
        CoffeeCup,
        Calendar,
        Edit,
        Delete
      };
    },
  };
  </script>
  
  <style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  
  .timetable-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Inter', sans-serif;
    
    .header-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      
      .page-title {
        font-size: 28px;
        font-weight: 600;
        color: var(--el-color-primary);
        margin: 0;
      }
      
      .action-buttons {
        display: flex;
        gap: 12px;
      }
    }
    
    .control-panel {
      margin-bottom: 24px;
      border-radius: 12px;
      
      .controls {
        display: flex;
        gap: 16px;
        align-items: center;
        flex-wrap: wrap;
        
        .view-selector, .class-selector, .section-selector {
          width: 180px;
        }
        
        .navigation-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          
          .current-period {
            font-weight: 500;
            color: var(--el-text-color-primary);
            min-width: 220px;
            text-align: center;
            font-size: 15px;
          }
          
          .nav-button {
            font-size: 16px;
            padding: 8px;
            color: var(--el-color-primary);
            border: 1px solid var(--el-border-color);
            transition: all 0.3s ease;
            
            &:hover {
              background-color: var(--el-color-primary-light-9);
              transform: scale(1.05);
            }
          }
        }
      }
    }
    
    .day-view, .week-view {
      border-radius: 12px;
      overflow: hidden;
      transition: all 0.3s ease;
      
      &:hover {
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
      }
    }
    
    .day-title, .week-title {
      color: var(--el-text-color-primary);
      font-weight: 600;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 8px;
      
      &::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 20px;
        background-color: var(--el-color-primary);
        border-radius: 2px;
      }
    }
    
    /* Day View Grid Layout */
    .schedule-grid {
      display: grid;
      grid-template-columns: 140px 1fr;
      border: 1px solid var(--el-border-color-light);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
    
    .time-column, .periods-column {
      display: grid;
      grid-template-rows: repeat(auto-fill, 70px);
    }
    
    .time-header, .period-header {
      padding: 12px;
      background-color: var(--el-color-primary);
      color: white;
      font-weight: 500;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 15px;
      
      .el-icon {
        font-size: 18px;
      }
    }
    
    .time-slot {
      padding: 12px;
      border-bottom: 1px solid var(--el-border-color-lighter);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--el-fill-color-lighter);
      font-size: 14px;
      color: var(--el-text-color-regular);
      font-weight: 500;
    }
    
    .period-slot {
      padding: 8px;
      border-bottom: 1px solid var(--el-border-color-lighter);
      min-height: 70px;
      transition: all 0.2s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        z-index: 1;
      }
    }
    
    .empty-slot {
      background-color: var(--el-fill-color-light);
    }
    
    .period-card {
      height: 100%;
      padding: 12px;
      border-radius: 6px;
      background: linear-gradient(135deg, var(--el-color-primary), var(--el-color-primary-light-3));
      color: white;
      display: flex;
      flex-direction: column;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      position: relative;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .period-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        padding: 0;
        background: transparent;
        text-align: left;
      }
      
      .period-subject {
        font-weight: 600;
        font-size: 15px;
        flex-grow: 1;
      }
    }
    
    .period-actions {
      display: flex;
      gap: 4px;
      
      .el-button {
        padding: 4px;
        opacity: 0;
        transition: opacity 0.2s ease;
      }
    }
    
    .period-card:hover .period-actions .el-button,
    .week-period-card:hover .period-actions .el-button {
      opacity: 1;
    }
    
    .period-details {
      font-size: 13px;
      opacity: 0.9;
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .period-teacher, .period-class-section {
        display: flex;
        align-items: center;
        gap: 4px;
        
        .el-icon {
          font-size: 14px;
        }
      }
    }
    
    .free-period {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--el-text-color-secondary);
      font-style: italic;
      gap: 6px;
      font-size: 14px;
      
      .el-icon {
        color: var(--el-color-info);
      }
    }
    
    /* Week View Grid Layout */
    .week-schedule {
      display: grid;
      grid-template-columns: 140px repeat(7, 1fr);
      border: 1px solid var(--el-border-color-light);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
    
    .week-time-column {
      display: grid;
      grid-template-rows: 90px repeat(auto-fill, 70px);
    }
    
    .corner-cell {
      background-color: var(--el-color-primary);
    }
    
    .week-time-slot {
      padding: 12px;
      border-bottom: 1px solid var(--el-border-color-lighter);
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--el-fill-color-lighter);
      font-size: 13px;
      color: var(--el-text-color-regular);
      font-weight: 500;
    }
    
    .week-day-column {
      display: grid;
      grid-template-rows: 90px repeat(auto-fill, 70px);
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        z-index: 1;
      }
    }
    
    .week-day-column.holiday-column {
      background-color: var(--el-color-warning-light-9);
    }
    
    .day-header {
      padding: 12px;
      text-align: center;
      border-bottom: 1px solid var(--el-border-color-lighter);
      background-color: var(--el-fill-color-lighter);
      
      h3 {
        margin: 0 0 6px 0;
        font-size: 15px;
        font-weight: 600;
        color: var(--el-text-color-primary);
      }
      
      div {
        font-size: 13px;
        color: var(--el-text-color-regular);
        margin-bottom: 6px;
      }
    }
    
    .holiday-badge {
      margin-top: 6px;
    }
    
    .week-period-slot {
      padding: 8px;
      border-bottom: 1px solid var(--el-border-color-lighter);
      border-right: 1px solid var(--el-border-color-lighter);
      min-height: 70px;
      transition: all 0.2s ease;
    }
    
    .week-period-slot.holiday-slot {
      background-color: var(--el-color-warning-light-9);
    }
    
    .week-period-card {
      height: 100%;
      padding: 12px;
      border-radius: 6px;
      background: linear-gradient(135deg, var(--el-color-success), var(--el-color-success-light-3));
      color: white;
      display: flex;
      flex-direction: column;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
      position: relative;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .period-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;
        padding: 0;
        background: transparent;
        text-align: left;
      }
      
      .week-period-subject {
        font-weight: 600;
        font-size: 14px;
        flex-grow: 1;
      }
    }
    
    .week-period-teacher {
      font-size: 12px;
      opacity: 0.9;
      display: flex;
      align-items: center;
      gap: 4px;
      
      .el-icon {
        font-size: 12px;
      }
    }
    
    .week-free-period {
      height: 100%;
      background-color: var(--el-fill-color-light);
    }
    
    .holiday-message {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: var(--el-color-warning);
      font-size: 13px;
      font-weight: 500;
      gap: 6px;
      
      .el-icon {
        font-size: 18px;
      }
    }
    
    .holiday-notice {
      padding: 24px;
      border-radius: 8px;
      text-align: center;
      margin: 20px 0;
      background-color: var(--el-color-warning-light-9);
      
      .holiday-title {
        color: var(--el-color-warning);
        margin-bottom: 8px;
        font-size: 18px;
      }
      
      p {
        color: var(--el-text-color-regular);
        margin-bottom: 12px;
      }
    }
    
    @media (max-width: 1200px) {
      .week-schedule {
        grid-template-columns: 120px repeat(7, minmax(100px, 1fr));
      }
    }
    
    @media (max-width: 992px) {
      .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
      }
      
      .controls {
        flex-direction: column;
        align-items: stretch;
      }
      
      .navigation-controls {
        justify-content: center;
      }
      
      .week-schedule {
        grid-template-columns: 100px repeat(7, minmax(80px, 1fr));
        overflow-x: auto;
      }
    }
    
    @media (max-width: 768px) {
      .schedule-grid {
        grid-template-columns: 100px 1fr;
      }
      
      .time-slot, .week-time-slot {
        font-size: 12px;
        padding: 8px;
      }
      
      .day-header h3 {
        font-size: 14px;
      }
      
      .week-period-card, .period-card {
        font-size: 13px;
        padding: 8px;
      }
      
      .week-title, .day-title {
        font-size: 20px;
      }
    }
    
    @media (max-width: 576px) {
      .timetable-container {
        padding: 12px;
      }
      
      .schedule-grid {
        grid-template-columns: 80px 1fr;
      }
      
      .time-header, .period-header {
        font-size: 14px;
        padding: 8px;
      }
      
      .time-slot, .week-time-slot {
        font-size: 11px;
      }
      
      .period-subject {
        font-size: 13px;
      }
      
      .period-details {
        font-size: 11px;
      }
    }
  }
  </style>